import { useState } from "react";
import {
  Armchair, Refrigerator, WashingMachine, Bed, Archive, Table, Tv, Trash2, Layers, Trees, Plus, Minus, X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const PRESETS = [
  { key: "sofa", name: "Sofa / Couch", icon: Armchair },
  { key: "fridge", name: "Fridge / Freezer", icon: Refrigerator },
  { key: "washer", name: "Washing Machine", icon: WashingMachine },
  { key: "mattress", name: "Double Mattress", icon: Bed },
  { key: "wardrobe", name: "Wardrobe / Chest", icon: Archive },
  { key: "dining", name: "Dining Table & Chairs", icon: Table },
  { key: "tv", name: "TV & Electronics", icon: Tv },
  { key: "rubbish", name: "General Rubbish Bags", icon: Trash2 },
  { key: "carpet", name: "Carpet / Flooring", icon: Layers },
  { key: "garden", name: "Garden Waste", icon: Trees },
];

export default function Step3Items({ data, update }) {
  const [customName, setCustomName] = useState("");
  const items = data.items || [];

  function findIdx(name) {
    return items.findIndex((it) => it.name === name);
  }

  function togglePreset(name) {
    const idx = findIdx(name);
    if (idx >= 0) {
      const next = items.slice();
      next.splice(idx, 1);
      update({ items: next });
    } else {
      update({ items: [...items, { name, quantity: 1 }] });
    }
  }

  function updateItem(idx, patch) {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    update({ items: next });
  }

  function removeItem(idx) {
    const next = items.slice();
    next.splice(idx, 1);
    update({ items: next });
  }

  function addCustom() {
    const n = customName.trim();
    if (!n) return;
    if (findIdx(n) >= 0) {
      setCustomName("");
      return;
    }
    update({ items: [...items, { name: n, quantity: 1 }] });
    setCustomName("");
  }

  return (
    <div className="space-y-6" data-testid="step-3-items">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Items to collect</h2>
        <p className="mt-2 text-slate-600">
          Tap categories to add and adjust quantities. Add anything else as a custom item.
        </p>
      </div>

      {/* Preset chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="preset-chips">
        {PRESETS.map(({ key, name, icon: Icon }) => {
          const active = findIdx(name) >= 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => togglePreset(name)}
              data-testid={`preset-${key}`}
              className={`group flex items-center gap-2 px-3 h-12 rounded-xl border text-sm font-medium transition-all text-left ${
                active
                  ? "chip-selected"
                  : "border-slate-200 bg-white text-slate-700 hover:border-[#FFB870] hover:bg-[#FFF7EE]"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{name}</span>
            </button>
          );
        })}
      </div>

      {/* Custom item add */}
      <div className="rounded-2xl bg-white border border-slate-200 p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Label htmlFor="custom" className="text-slate-700 font-medium">Add a custom item</Label>
          <Input
            id="custom"
            data-testid="input-custom-item"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
            placeholder="e.g. Piano, Shed contents, BBQ"
            className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-[#FF7F00]"
          />
        </div>
        <Button
          type="button"
          onClick={addCustom}
          data-testid="btn-add-custom"
          className="bg-[#0F172A] hover:bg-[#1F2937] text-white h-11 sm:mt-6 rounded-lg px-5 font-semibold"
        >
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>

      {/* Selected items list */}
      <div className="space-y-3" data-testid="selected-items-list">
        {items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm bg-white">
            No items selected yet. Tap a category above or add a custom one.
          </div>
        )}
        {items.map((it, idx) => (
          <div
            key={`${it.name}-${idx}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex items-center gap-3"
            data-testid={`item-row-${idx}`}
          >
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-slate-900 truncate">{it.name}</p>
              <div className="mt-2 inline-flex items-center gap-1 bg-[#FFF3E6] border border-[#FFB870] rounded-full p-1">
                <button
                  type="button"
                  onClick={() => updateItem(idx, { quantity: Math.max(1, it.quantity - 1) })}
                  data-testid={`item-qty-dec-${idx}`}
                  className="h-7 w-7 rounded-full bg-white text-[#FF7F00] flex items-center justify-center hover:bg-orange-50"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[28px] text-center text-sm font-bold text-slate-900" data-testid={`item-qty-${idx}`}>
                  {it.quantity}
                </span>
                <button
                  type="button"
                  onClick={() => updateItem(idx, { quantity: it.quantity + 1 })}
                  data-testid={`item-qty-inc-${idx}`}
                  className="h-7 w-7 rounded-full bg-[#FF7F00] text-white flex items-center justify-center hover:bg-[#E67000]"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeItem(idx)}
              data-testid={`item-remove-${idx}`}
              className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center justify-center shrink-0"
              aria-label="Remove"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
